import { flow, getRoot, type Instance, types } from 'mobx-state-tree';

import { markApplicationsRead } from 'src/graphql/hirer/markApplicationsRead';
import type { FiltersEventCaptureData } from 'src/services/jobApplicationService/buildCaptureEventData';

import { STATUS_CHANGE_LOCATION } from '../../graphql/hirer/updateApplicationsStatus';
import { sendStatusUpdateCaptureEvent } from '../../services/jobApplicationService/sendStatusUpdateCaptureEvent';
import type { IStore } from '../Store';

import { BUCKETS } from './types';

import type { IJobApplicationStore } from './index';

const JobApplicationSummary = types
  .model('JobApplicationSummary', {
    /**
     * id
     *
     * @description prospectId
     */
    id: types.identifier,
    applicationCorrelationId: (types.string, ''),
    firstName: types.optional(types.string, ''),
    lastName: types.optional(types.string, ''),
    selected: types.optional(types.boolean, false),
    isRead: types.optional(types.boolean, true),
    isMarkedAsRemoved: types.optional(types.boolean, false),
    positionInList: types.optional(types.number, 0),
  })
  .views((self) => ({
    get rootStore(): IStore {
      return getRoot(self);
    },

    get fullName() {
      return `${self.firstName} ${self.lastName}`;
    },
    get shortDisplayName() {
      if (self.firstName !== '') {
        return self.firstName;
      }
      if (self.lastName !== '') {
        return self.lastName;
      }
      return '';
    },
  }))
  .views((self) => ({
    get jobApplicationStore(): IJobApplicationStore {
      return self.rootStore.jobApplicationStore;
    },
  }))
  .views((self) => ({
    get positionId() {
      return self.jobApplicationStore.positionId;
    },

    get isUnread() {
      return !self.isRead;
    },
  }))
  .actions((self) => {
    const setRead = () => {
      if (self.isUnread) {
        self.isRead = true;
      }
    };

    const setReadWithCountUpdated = () => {
      if (self.isUnread) {
        self.isRead = true;
        self.jobApplicationStore.bucketStatsStore.bucketStats.decrementNewCount();
      }
    };

    return {
      setRead,
      setReadWithCountUpdated,
    };
  })
  .actions((self) => {
    const markRead = flow(function* ({
      filtersEventCaptureData,
    }: {
      filtersEventCaptureData: FiltersEventCaptureData;
    }) {
      if (self.isUnread) {
        const markResult = yield markApplicationsRead([self.id]);
        if (markResult) {
          self.setReadWithCountUpdated();
          sendStatusUpdateCaptureEvent({
            statusChangeLocation: STATUS_CHANGE_LOCATION.CANDIDATE_DETAIL,
            jobApplications: [self as IJobApplicationSummary],
            store: self.rootStore,
            destinationBucket: BUCKETS.INBOX,
            filters: filtersEventCaptureData,
          });
        }
      }
    });

    const markRemoved = () => {
      if (!self.jobApplicationStore.hasSearchText) {
        self.isMarkedAsRemoved = true;
      }
    };

    return {
      markRead,
      markRemoved,
    };
  });

export interface IJobApplicationSummary
  extends Instance<typeof JobApplicationSummary> {}

export default JobApplicationSummary;
